import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { authorTitle } from '@/shared/lib/authorTitle';
import { CONTENT_COLUMN, listColumn } from '@/shared/layout/breakpoints';
import * as booksApi from '@/api/books';
import type { AuthorHit } from '@/api/books';
import { useAuth } from '@/context/AuthContext';
import { useAuthor } from '@/context/AuthorContext';
import { useSearchBar } from '@/context/SearchBarContext';
import BookPagination from '@/features/catalogue/BookPagination';
import SkeletonCard from '@/features/catalogue/SkeletonCard';

const PAGE_SIZE = 10;

/**
 * AuthorSearch lists the authors matching a name from the search panel.
 *
 * It is a stop on the way to a book list rather than a destination, and the
 * reader is picking which of several same-named authors they meant. A column of
 * near-identical names gives them nothing to pick with, so each row also says
 * how many books are behind it — which is usually the whole answer, one
 * Dostoyevsky holding 184 books beside four holding one apiece.
 *
 * The count is taken under the reader's own books language, the same filter the
 * list behind the row uses, so the number is what they will actually get.
 */
const AuthorSearch: React.FC = () => {
    const { t } = useTranslation();
    const { page } = useParams<{ page: string }>();
    const { author } = useParams<{ author: string }>();
    const [authors, setAuthors] = useState<AuthorHit[]>([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const [totalPages, setTotalPages] = useState(0);
    const baseUrl = window.location.pathname.replace(/\/\d+$/, '');
    const navigate = useNavigate();
    const { setSearchItem } = useSearchBar();
    const { user } = useAuth();
    const { setAuthorName } = useAuthor();

    useEffect(() => {
        const fetchAuthors = async () => {
            setLoading(true);
            try {
                window.scrollTo(0, 0);
                const currentPage = parseInt(page || '1', 10);

                const responseData = await booksApi.listAuthors({
                    limit: PAGE_SIZE,
                    offset: (currentPage - 1) * PAGE_SIZE,
                    author: decodeURIComponent(author || ''),
                    lang: user?.books_lang || '',
                });

                // A zero-hit search answers authors: null (a nil Go slice
                // marshals to null). Anything that is not an array means "no
                // results": skipping the update would leave the previous
                // search's authors and page count on screen.
                if (Array.isArray(responseData.authors)) {
                    setAuthors(responseData.authors);
                    setTotalPages(responseData.length);
                } else {
                    setAuthors([]);
                    setTotalPages(0);
                }
            } catch (error) {
                console.error('Error fetching authors:', error);
                setAuthors([]); // Set to an empty array in case of error
            } finally {
                setLoading(false);
            }
        };

        if (author) {
            fetchAuthors().then((r) => r);
        }
    }, [author, location.search, page, user?.books_lang]);

    /** Clicking through leaves the search box empty so the filter, not a stale query, applies. */
    const handleAuthorClick = (authorId: number | string, fullName: string) => {
        setSearchItem('');
        // Carried over so the scope beside the search box can name the author
        // without asking the server for what was just on screen.
        setAuthorName(fullName);
        navigate(`/books/find/author/${authorId}/1`);
    };

    return (
        <div className="flex min-h-[calc(100vh-200px)] flex-col">
            {/*
              One column element, holding the rows and the pager together. The
              pager fills its parent and fits its window to what that row
              measures; it used to sit outside this column, in the page's
              full-width root, so on a wide viewport it measured the viewport
              and put its arrows a couple of hundred pixels clear of the rows.
            */}
            <div className={cn(CONTENT_COLUMN, 'flex flex-1 flex-col')} {...listColumn}>
                <div className="py-2">
                    {loading ? (
                        <div className="flex flex-col gap-1.5">
                            {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                                <SkeletonCard key={index} />
                            ))}
                        </div>
                    ) : authors.length === 0 ? (
                        <div className="rounded border border-border bg-card p-8">
                            <p className="text-center text-lg">{t('noAuthorsFound')}</p>
                        </div>
                    ) : (
                        <div className="rounded border border-border bg-card p-4">
                            <h2 className="mb-2 text-center text-2xl font-medium">
                                {t('authorsSearch')}
                            </h2>
                            {/* Tailwind runs without preflight, so a bare <ul> keeps
                                both its markers and the browser's 40px inline start
                                padding, which would indent every author. */}
                            <ul className="list-none p-0">
                                {authors.map((author) => (
                                    <li key={author.id}>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleAuthorClick(author.id, authorTitle(author, t))
                                            }
                                            className="flex w-full items-baseline justify-between gap-3 rounded px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground"
                                        >
                                            <span>
                                                {author.full_name}
                                                {/* An entity with a namesake says which one it is. */}
                                                {/* A namesake of another genre says which one it is; one of
                                                    the same genre is told apart by the count
                                                    beside it, the books the row opens. */}
                                                {author.label && (
                                                    <span className="ml-1.5 text-sm text-muted-foreground">
                                                        · {author.label}
                                                    </span>
                                                )}
                                            </span>
                                            {author.books_count ? (
                                                /* Kept off the name's baseline
                                                   run so a long name wraps
                                                   without pushing the number
                                                   onto its own line. */
                                                <span className="shrink-0 text-sm whitespace-nowrap text-muted-foreground">
                                                    {t('bookCount', {
                                                        count: author.books_count,
                                                    })}
                                                </span>
                                            ) : null}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="mt-auto pt-4">
                    <BookPagination
                        totalPages={totalPages}
                        currentPage={parseInt(page || '1', 10)}
                        baseUrl={baseUrl}
                    />
                </div>
            </div>
        </div>
    );
};

export default AuthorSearch;
